import type { Document } from "../document/Document";
import type { SmartFilterEntry, SmartObjectLayer } from "../document/Layer";
import type { Command } from "./Command";

/**
 * Apply a smart filter to a Smart Object. The filter is added to the end
 * of the Smart Object's smartFilters array. The filter is non-destructive —
 * the embedded document pixels are never modified; the filter is applied
 * at render time.
 *
 * Photoshop equivalent: Filter → [any filter] on a Smart Object layer.
 */
export class ApplySmartFilterCommand implements Command {
  readonly kind = "apply-smart-filter";

  constructor(
    private readonly smartObjectId: string,
    private readonly filterId: string,
    private readonly params: Record<string, number | string | boolean>,
    private readonly filterEntryId?: string,
    private readonly enabled?: boolean,
    private readonly opacity?: number,
    private readonly blendMode?: import("../document/Layer").BlendMode,
  ) {}

  apply(doc: Document): Document {
    const newLayers = addFilterToSmartObject(
      doc.layers,
      this.smartObjectId,
      this.filterId,
      this.params,
      this.filterEntryId,
      this.enabled,
      this.opacity,
      this.blendMode,
    );
    if (newLayers === doc.layers) return doc;
    return { ...doc, layers: newLayers };
  }

  invert(_before: Document, _after: Document): Command {
    return new RemoveSmartFilterCommand(this.smartObjectId, this.filterEntryId ?? "");
  }
}

/**
 * Remove a smart filter from a Smart Object's filter stack.
 */
export class RemoveSmartFilterCommand implements Command {
  readonly kind = "remove-smart-filter";

  constructor(
    private readonly smartObjectId: string,
    private readonly filterEntryId: string,
  ) {}

  apply(doc: Document): Document {
    const newLayers = removeFilterFromSmartObject(
      doc.layers,
      this.smartObjectId,
      this.filterEntryId,
    );
    if (newLayers === doc.layers) return doc;
    return { ...doc, layers: newLayers };
  }

  invert(before: Document, _after: Document): Command {
    // Find the filter entry we removed to restore it with its original state
    const so = findSmartObject(before.layers, this.smartObjectId);
    if (!so) return new RemoveSmartFilterCommand(this.smartObjectId, this.filterEntryId);
    const entry = so.smartFilters.find(f => f.id === this.filterEntryId);
    if (!entry) return new RemoveSmartFilterCommand(this.smartObjectId, this.filterEntryId);
    return new ApplySmartFilterCommand(
      this.smartObjectId,
      entry.filterId,
      entry.params,
      entry.id,
      entry.enabled,
      entry.opacity,
      entry.blendMode,
    );
  }
}

/**
 * Reorder smart filters within a Smart Object's filter stack.
 */
export class ReorderSmartFilterCommand implements Command {
  readonly kind = "reorder-smart-filter";

  constructor(
    private readonly smartObjectId: string,
    private readonly filterEntryId: string,
    private readonly newIndex: number,
    private readonly oldIndex: number,
  ) {}

  apply(doc: Document): Document {
    const newLayers = reorderFilterInSmartObject(
      doc.layers,
      this.smartObjectId,
      this.filterEntryId,
      this.newIndex,
    );
    if (newLayers === doc.layers) return doc;
    return { ...doc, layers: newLayers };
  }

  invert(_before: Document, _after: Document): Command {
    return new ReorderSmartFilterCommand(
      this.smartObjectId,
      this.filterEntryId,
      this.oldIndex,
      this.newIndex,
    );
  }
}

/**
 * Toggle a smart filter's enabled state.
 */
export class ToggleSmartFilterCommand implements Command {
  readonly kind = "toggle-smart-filter";

  constructor(
    private readonly smartObjectId: string,
    private readonly filterEntryId: string,
  ) {}

  apply(doc: Document): Document {
    const newLayers = toggleFilterInSmartObject(doc.layers, this.smartObjectId, this.filterEntryId);
    if (newLayers === doc.layers) return doc;
    return { ...doc, layers: newLayers };
  }

  invert(_before: Document, _after: Document): Command {
    return new ToggleSmartFilterCommand(this.smartObjectId, this.filterEntryId);
  }
}

/**
 * Update a smart filter's parameters.
 */
export class UpdateSmartFilterCommand implements Command {
  readonly kind = "update-smart-filter";

  constructor(
    private readonly smartObjectId: string,
    private readonly filterEntryId: string,
    private readonly params: Record<string, number | string | boolean>,
    private readonly prevParams?: Record<string, number | string | boolean>,
  ) {}

  apply(doc: Document): Document {
    const newLayers = updateFilterParams(doc.layers, this.smartObjectId, this.filterEntryId, this.params);
    if (newLayers === doc.layers) return doc;
    return { ...doc, layers: newLayers };
  }

  invert(_before: Document, _after: Document): Command {
    return new UpdateSmartFilterCommand(
      this.smartObjectId,
      this.filterEntryId,
      this.prevParams ?? {},
      this.params,
    );
  }
}

// ---------------------------------------------------------------------------
// Tree mutation helpers
// ---------------------------------------------------------------------------

function addFilterToSmartObject(
  layers: readonly import("../document/Layer").Layer[],
  smartObjectId: string,
  filterId: string,
  params: Record<string, number | string | boolean>,
  entryId?: string,
  enabled?: boolean,
  opacity?: number,
  blendMode?: import("../document/Layer").BlendMode,
): readonly import("../document/Layer").Layer[] {
  for (let i = 0; i < layers.length; i++) {
    const l = layers[i]!;
    if (l.kind === "smart-object" && l.id === smartObjectId) {
      const entry: SmartFilterEntry = {
        id: entryId || `sf-${Date.now()}`,
        filterId,
        params,
        enabled: enabled ?? true,
        opacity: opacity ?? 1,
        blendMode: blendMode ?? "normal",
      };
      const newLayers = [...layers];
      newLayers[i] = { ...l, smartFilters: [...l.smartFilters, entry] };
      return newLayers;
    }
    if (l.kind === "group") {
      const newChildren = addFilterToSmartObject(l.children, smartObjectId, filterId, params, entryId);
      if (newChildren !== l.children) {
        const newLayers = [...layers];
        newLayers[i] = { ...l, children: newChildren };
        return newLayers;
      }
    }
  }
  return layers;
}

function removeFilterFromSmartObject(
  layers: readonly import("../document/Layer").Layer[],
  smartObjectId: string,
  filterEntryId: string,
): readonly import("../document/Layer").Layer[] {
  for (let i = 0; i < layers.length; i++) {
    const l = layers[i]!;
    if (l.kind === "smart-object" && l.id === smartObjectId) {
      const filtered = l.smartFilters.filter(f => f.id !== filterEntryId);
      if (filtered.length === l.smartFilters.length) return layers;
      const newLayers = [...layers];
      newLayers[i] = { ...l, smartFilters: filtered };
      return newLayers;
    }
    if (l.kind === "group") {
      const newChildren = removeFilterFromSmartObject(l.children, smartObjectId, filterEntryId);
      if (newChildren !== l.children) {
        const newLayers = [...layers];
        newLayers[i] = { ...l, children: newChildren };
        return newLayers;
      }
    }
  }
  return layers;
}

function reorderFilterInSmartObject(
  layers: readonly import("../document/Layer").Layer[],
  smartObjectId: string,
  filterEntryId: string,
  newIndex: number,
): readonly import("../document/Layer").Layer[] {
  for (let i = 0; i < layers.length; i++) {
    const l = layers[i]!;
    if (l.kind === "smart-object" && l.id === smartObjectId) {
      const idx = l.smartFilters.findIndex(f => f.id === filterEntryId);
      if (idx < 0) return layers;
      const filters = [...l.smartFilters];
      const [moved] = filters.splice(idx, 1);
      filters.splice(newIndex, 0, moved!);
      const newLayers = [...layers];
      newLayers[i] = { ...l, smartFilters: filters };
      return newLayers;
    }
    if (l.kind === "group") {
      const newChildren = reorderFilterInSmartObject(l.children, smartObjectId, filterEntryId, newIndex);
      if (newChildren !== l.children) {
        const newLayers = [...layers];
        newLayers[i] = { ...l, children: newChildren };
        return newLayers;
      }
    }
  }
  return layers;
}

function toggleFilterInSmartObject(
  layers: readonly import("../document/Layer").Layer[],
  smartObjectId: string,
  filterEntryId: string,
): readonly import("../document/Layer").Layer[] {
  for (let i = 0; i < layers.length; i++) {
    const l = layers[i]!;
    if (l.kind === "smart-object" && l.id === smartObjectId) {
      const filters = l.smartFilters.map(f =>
        f.id === filterEntryId ? { ...f, enabled: !f.enabled } : f,
      );
      const newLayers = [...layers];
      newLayers[i] = { ...l, smartFilters: filters };
      return newLayers;
    }
    if (l.kind === "group") {
      const newChildren = toggleFilterInSmartObject(l.children, smartObjectId, filterEntryId);
      if (newChildren !== l.children) {
        const newLayers = [...layers];
        newLayers[i] = { ...l, children: newChildren };
        return newLayers;
      }
    }
  }
  return layers;
}

function updateFilterParams(
  layers: readonly import("../document/Layer").Layer[],
  smartObjectId: string,
  filterEntryId: string,
  params: Record<string, number | string | boolean>,
): readonly import("../document/Layer").Layer[] {
  for (let i = 0; i < layers.length; i++) {
    const l = layers[i]!;
    if (l.kind === "smart-object" && l.id === smartObjectId) {
      const filters = l.smartFilters.map(f =>
        f.id === filterEntryId ? { ...f, params } : f,
      );
      const newLayers = [...layers];
      newLayers[i] = { ...l, smartFilters: filters };
      return newLayers;
    }
    if (l.kind === "group") {
      const newChildren = updateFilterParams(l.children, smartObjectId, filterEntryId, params);
      if (newChildren !== l.children) {
        const newLayers = [...layers];
        newLayers[i] = { ...l, children: newChildren };
        return newLayers;
      }
    }
  }
  return layers;
}

function findSmartObject(
  layers: readonly import("../document/Layer").Layer[],
  id: string,
): SmartObjectLayer | null {
  for (const l of layers) {
    if (l.kind === "smart-object" && l.id === id) return l;
    if (l.kind === "group") {
      const inner = findSmartObject(l.children, id);
      if (inner) return inner;
    }
  }
  return null;
}
